import React from 'react';
import styled from 'styled-components';

import { SortChevronIcon } from '../../svgs/SortChevronIcon';

import type { ReactNode } from 'react';

/* ─── Table Root ─── */
interface TableProps {
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function Table({
  children,
  className,
  'data-testid': testId,
}: TableProps) {
  return (
    <TableWrapper className={className} data-testid={testId}>
      <StyledTable>{children}</StyledTable>
    </TableWrapper>
  );
}

/* ─── Table Header ─── */
export function TableHeader({ children }: { children: ReactNode }) {
  return <StyledTHead>{children}</StyledTHead>;
}

/* ─── Table Body ─── */
export function TableBody({ children }: { children: ReactNode }) {
  return <StyledTBody>{children}</StyledTBody>;
}

/* ─── Table Row ─── */
interface TableRowProps {
  children: ReactNode;
  onClick?: () => void;
  isLastRow?: boolean;
  className?: string;
  'data-testid'?: string;
}

export function TableRow({
  children,
  onClick,
  isLastRow,
  className,
  'data-testid': testId,
}: TableRowProps) {
  return (
    <StyledTR
      $clickable={!!onClick}
      $isLastRow={isLastRow}
      onClick={onClick}
      className={className}
      data-testid={testId}
    >
      {children}
    </StyledTR>
  );
}

/* ─── Table Head Cell ─── */
interface TableHeadProps {
  children?: ReactNode;
  align?: 'left' | 'center' | 'right';
  className?: string;
  sortDirection?: 'asc' | 'desc' | null;
  onSort?: () => void;
}

export function TableHead({
  children,
  align = 'left',
  className,
  sortDirection,
  onSort,
}: TableHeadProps) {
  return (
    <StyledTH
      $align={align}
      $sortable={!!onSort}
      className={className}
      onClick={onSort}
    >
      {children}
      {onSort && (
        <SortIconWrapper
          $active={!!sortDirection}
          $desc={sortDirection === 'desc'}
          aria-hidden="true"
        >
          <SortChevronIcon />
        </SortIconWrapper>
      )}
    </StyledTH>
  );
}

/* ─── Table Cell ─── */
interface TableCellProps {
  children?: ReactNode;
  align?: 'left' | 'center' | 'right';
  className?: string;
}

export const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ children, align = 'left', className }, ref) => {
    return (
      <StyledTD $align={align} className={className} ref={ref}>
        {children}
      </StyledTD>
    );
  },
);
TableCell.displayName = 'TableCell';

/* ─── Styled Components ─── */

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  /* Fixed layout makes columns honor their declared widths and fit the
     container, so long cell content truncates (see the cells' ellipsis
     styles) instead of widening the table and forcing a horizontal
     scrollbar. Consumers must give every column a width. */
  table-layout: fixed;
`;

const StyledTHead = styled.thead``;

const StyledTBody = styled.tbody``;

const StyledTR = styled.tr<{ $clickable?: boolean; $isLastRow?: boolean }>`
  cursor: ${props => (props.$clickable ? 'pointer' : 'default')};
  border-bottom: 1px solid
    color-mix(in srgb, var(--text-muted) 40%, transparent);
  transition: background-color var(--transition-fast);

  ${props =>
    props.$clickable &&
    `
    &:hover {
      background-color: color-mix(in srgb, var(--border) 40%, transparent);
    }
  `}
`;

const StyledTH = styled.th<{ $align: string; $sortable?: boolean }>`
  padding: var(--table-header-padding-y) var(--table-cell-padding-x);
  text-align: ${props => props.$align};
  font-family: var(--font-primary);
  /* Column labels sit one step below the 14px row content so headers read as
     labels, not titles. See "Tables" in docs/design-system.md. */
  font-size: var(--text-xs);
  font-weight: var(--font-weight-normal);
  line-height: var(--leading-text-sm);
  color: var(--text-muted);
  white-space: nowrap;
  text-transform: none;
  ${props =>
    props.$sortable &&
    `
    cursor: pointer;
    user-select: none;
    &:hover {
      color: var(--foreground);
    }
  `}
`;

const SortIconWrapper = styled.span<{ $active: boolean; $desc?: boolean }>`
  display: inline-flex;
  align-items: center;
  margin-left: var(--spacing-sm);
  color: var(--text-muted);
  vertical-align: middle;
  opacity: ${props => (props.$active ? 1 : 0)};
  transition:
    opacity var(--transition-fast),
    transform var(--transition-fast);
  transform: ${props => (props.$desc ? 'rotate(180deg)' : 'rotate(0deg)')};

  th:hover & {
    opacity: 1;
  }
`;

const StyledTD = styled.td<{ $align: string }>`
  padding: var(--table-row-padding-y) var(--table-cell-padding-x);
  text-align: ${props => props.$align};
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);
  line-height: var(--leading-text-sm);
  color: var(--foreground);
  vertical-align: middle;
`;
